import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet'
import { useFeatureGate } from '@funkit/connect-core'
import { useState } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { FlowSheetBackground } from '../flow/FlowSheet'
import {
  isUsableWalletAddress,
  useFunkitIdentity,
} from '../identity/IdentityContext'
import type { FlowNav, FlowScreen } from '../flow/FunkitFlowProvider'
import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from '../flow/withdrawalFlowConfig'
import { useIsStablecoin } from '../hooks/useIsStablecoin'
import { useNestedSheet } from '../hooks/useNestedSheet'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import type { SafeClipboardReader } from '../transfer/clipboard'
import {
  formatTokenAmount,
  formatUsd,
  groupThousands,
} from '../utils/formatAmount'
import { useCryptoWithdrawalForm } from '../withdrawal/useCryptoWithdrawalForm'
import type { DepositAddressOrHash } from '../withdrawal/useCryptoWithdrawalExecution'
import { useCryptoWithdrawalSubmit } from '../withdrawal/useCryptoWithdrawalSubmit'
import { SelectReceiveTokenAndChainScreen } from './SelectReceiveTokenAndChainScreen'
import { SelectSourceTokenScreen } from './SelectSourceTokenScreen'
import { TransactionBreakdownScreen } from './TransactionBreakdownScreen'
import { WithdrawAddressScreen } from './WithdrawAddressScreen'
import { WithdrawAmountScreen } from './WithdrawAmountScreen'

/** What is presented as a sheet over whichever step opened it. */
type WithdrawalNestedScreen = 'transactionBreakdown'

/**
 * The crypto rail's screens, and the one withdrawal they are all building.
 *
 * Each screen is its own entry in the flow's stack — `back` leaves the screen,
 * not the rail — while the state behind them lives here, so navigating between
 * them keeps the amount, the source and the quote. A screen added later (the
 * recipient's address is next) is a case below and a `nav.navigate` from the one
 * before it.
 */
export function WithdrawCryptoSteps({
  config,
  callbacks,
  current,
  nav,
  quoteInputFeeTokens,
  quoteInputFeeSettled = true,
  extraSourceGasUsd,
  pasteFromClipboard,
  onSubmitted,
}: {
  config: WithdrawalFlowConfig
  callbacks: WithdrawalFlowCallbacks
  current: FlowScreen | undefined
  nav: FlowNav
  /**
   * A flat fee the venue takes off the withdrawal before it is quoted, in
   * source-token units, or 0. The SDK only knows the number, never who charges
   * it.
   */
  quoteInputFeeTokens: number
  quoteInputFeeSettled?: boolean
  /**
   * A venue fee billed as source-chain gas, in USD, for the summary's network
   * row, or 0.
   */
  extraSourceGasUsd: number
  /** Fills the recipient field from the clipboard. Guarded upstream. */
  pasteFromClipboard: SafeClipboardReader
  onSubmitted: (result: {
    depositAddressOrHash: DepositAddressOrHash
    amountLabel: string
    /** What arrived, frozen here: the quote refreshes, the outcome doesn't. */
    usdLabel: string | undefined
    tokenLabel: string | undefined
  }) => void
}) {
  const { t } = useFunkitTranslation()
  const insets = useSafeAreaInsets()
  const formOptions = { config, quoteInputFeeTokens, quoteInputFeeSettled }
  const form = useCryptoWithdrawalForm(formOptions)
  const {
    nestedSheetRef,
    nestedScreen,
    setNestedScreen,
    openNestedScreen,
    renderNestedBackdrop,
  } = useNestedSheet<WithdrawalNestedScreen>()
  const connectedWalletAddress = useConnectedRecipientAddress(config)
  // A 1:1 stablecoin reads the same either way round, so the swap between them
  // is a fake exchange rate — the same reason web hides it (`InputAmountLayout`).
  const isStablecoin = useIsStablecoin()
  const hideStablecoinSwap =
    useFeatureGate('hide-stablecoin-amount-switch') &&
    isStablecoin(form.sourceToken.symbol)
  const canSwapDenomination = form.canSwapDenomination && !hideStablecoinSwap
  // `undefined` when nothing prices the source, which sends both readers below
  // to token units instead.
  const amountUsdLabel =
    form.amountUsd === undefined ? undefined : formatUsd(Number(form.amountUsd))
  const receiveUsdLabel =
    form.receiveUsd === undefined ? undefined : formatUsd(form.receiveUsd)
  const receiveTokenLabel =
    form.receiveAmount && form.destinationToken
      ? `${formatTokenAmount(form.receiveAmount)} ${form.destinationToken.symbol}`
      : undefined
  const { submit, isSubmitting, isBusy, executionErrorText } =
    useCryptoWithdrawalSubmit({
      config,
      callbacks,
      form,
      // What the outcome reports on, frozen by the hook at the press: the quote
      // goes on refreshing and this step unmounts.
      receiveLabels: {
        usdLabel: receiveUsdLabel,
        tokenLabel: receiveTokenLabel,
      },
      onSubmitted,
    })
  // A picker over whichever screen opened it, not a step of its own: it is
  // reachable from the amount and from the details, and neither is where `back`
  // out of it should land.
  const [isPickingSource, setIsPickingSource] = useState(false)

  // A clipboard read can block for seconds on the OS permission prompt, so the
  // Paste pill is held down for the duration: a second tap would queue a second
  // read whose later result wins.
  const [isPasting, setIsPasting] = useState(false)

  if (isPickingSource) {
    return (
      <SelectSourceTokenScreen
        options={form.sourceOptions}
        selectedIndex={form.selectedSourceIndex}
        onSelect={(index) => {
          // The amount is cleared with the source, so there is nothing left to
          // review — the amount screen is where the user belongs.
          if (form.selectSource(index) && current !== 'withdrawAmount') {
            nav.popTo('withdrawAmount')
          }
          setIsPickingSource(false)
        }}
        onBack={() => setIsPickingSource(false)}
      />
    )
  }

  if (current === 'withdrawReceiveToken') {
    return (
      <SelectReceiveTokenAndChainScreen
        chains={form.destinationChains}
        committed={form.destinationToken}
        onBack={nav.back}
        onConfirm={(chainId, symbol) => {
          logger.info('withdrawal:destination_selected', {
            toChainId: chainId,
            toToken: symbol,
          })
          form.selectDestinationToken(chainId, symbol)
          nav.popTo('withdrawAddress')
        }}
      />
    )
  }

  if (current === 'withdrawAmount') {
    return (
      <WithdrawAmountScreen
        title={t('withdrawCrypto.title')}
        amount={form.amount}
        onChangeAmount={form.setAmount}
        input={form.input}
        onChangeInput={form.setInput}
        inUsd={form.inUsd}
        usdLabel={amountUsdLabel}
        onSwapDenomination={
          canSwapDenomination ? form.swapDenomination : undefined
        }
        sourceDecimals={form.sourceDecimals}
        balance={form.balance}
        source={{
          label: form.sourceLabel,
          symbol: form.sourceToken.symbol,
          chainId: Number(form.sourceToken.chainId),
          iconSrc: form.sourceOption.iconSrc,
        }}
        // Nothing to open with a single source; the receive token is picked on
        // the step that follows.
        onPressSource={
          form.sourceOptions.length > 1
            ? () => setIsPickingSource(true)
            : undefined
        }
        errorText={form.amountError}
        canContinue={form.canContinue}
        onContinue={() => {
          // Amount decision point, recorded where it is committed rather than on
          // every keystroke.
          logger.info('withdrawal:amount_confirmed', {
            fromChainId: form.sourceToken.chainId,
            fromToken: form.sourceToken.address,
            toChainId: form.destinationToken?.chainId,
            toToken: form.destinationToken?.address,
          })
          nav.navigate('withdrawAddress')
        }}
        onBack={nav.back}
        onClose={nav.closeFlow}
      />
    )
  }

  return (
    <>
      <WithdrawAddressScreen
        title={t('withdrawAddress.title', {
          amount:
            amountUsdLabel ??
            `${groupThousands(form.amount)} ${form.sourceToken.symbol}`,
        })}
        recipient={form.recipient}
        onChangeRecipient={form.setRecipient}
        isAddress={form.isRecipientAddress}
        isPasting={isPasting}
        onPaste={async () => {
          setIsPasting(true)
          try {
            // Test the trimmed value: a whitespace-only clipboard would
            // otherwise clear a recipient the user already entered.
            const pasted = (await pasteFromClipboard())?.trim()
            if (pasted) {
              form.setRecipient(pasted)
            }
          } finally {
            setIsPasting(false)
          }
        }}
        onUseConnected={
          connectedWalletAddress
            ? () => form.setRecipient(connectedWalletAddress)
            : undefined
        }
        errorText={form.recipientError}
        warningText={form.recipientWarning}
        destinationToken={form.destinationToken}
        onPressReceive={() => nav.navigate('withdrawReceiveToken')}
        usdLabel={receiveUsdLabel}
        tokenLabel={receiveTokenLabel}
        onPressBreakdown={() => openNestedScreen('transactionBreakdown')}
        // Without this the placeholders pulse on and the disabled button says
        // nothing — the one state the user can act on, by editing.
        quoteErrorText={
          form.isQuoteError ? t('withdrawCrypto.quoteFailed') : undefined
        }
        canSubmit={form.canSubmit}
        isBusy={isBusy}
        isSubmitting={isSubmitting}
        onSubmit={submit}
        executionErrorText={executionErrorText}
        onBack={nav.back}
        onClose={nav.closeFlow}
      />

      <BottomSheetModal
        ref={nestedSheetRef}
        // The breakdown is a paragraph, and sizes itself.
        enableDynamicSizing
        topInset={insets.top}
        handleComponent={null}
        backgroundComponent={FlowSheetBackground}
        backdropComponent={renderNestedBackdrop}
        onDismiss={() => setNestedScreen(null)}
      >
        <BottomSheetView>
          {nestedScreen === 'transactionBreakdown' ? (
            <TransactionBreakdownScreen
              usdLabel={receiveUsdLabel ?? ''}
              floorLabel={
                form.receiveFloorUsd === undefined
                  ? ''
                  : formatUsd(form.receiveFloorUsd)
              }
            />
          ) : null}
        </BottomSheetView>
      </BottomSheetModal>
    </>
  )
}

/**
 * The connected wallet's address, when it can stand in as the recipient: the
 * host can turn the shortcut off, and on a non-EVM rail the payer identity isn't
 * an address at all.
 */
function useConnectedRecipientAddress(
  config: WithdrawalFlowConfig,
): string | undefined {
  const { walletAddress } = useFunkitIdentity()
  if (config.disableConnectedWallet || !isUsableWalletAddress(walletAddress)) {
    return undefined
  }
  return walletAddress
}
